import { CSVLink } from 'react-csv';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Shot from "./shot";
import Errors from "../../../../shared/errors";
import {
  OnboardingStep1ClassName,
  OnboardingStep2ClassName,
  OnboardingStep3ClassName,
  OnboardingStep4ClassName
} from "./ducks/reducer";

import Onboarding from '../../../../shared/onboarding/onboarding';
import FilmanizeDropZone from '../../../../shared/filmanize-dropzone';
import confirm from "../../../../shared/modal/confirm";
import React from "react";
import FilmanizeCheckbox from "../../../../shared/filmanize-checkbox";
import Loader from '../../../../shared/loader';

export const Overview = (props) => {
  const {
    overview,
    filmId,
    errors,
    warnings,
    shotsOnboardingSteps,
    sceneId,
    pristine,
    updateShotOrder,
    saveShotsOrder,
    saving,
    csvData,
    sceneNumber,
    filmName,
    deleteMultipleShots,
    deleteing
  } = props;

  const readOnly = overview.readOnly;
  const [selectedShotIds, setSelectedShotIds] = React.useState<string[]>([]);

  const toggleSelection = (shotId: string) => {
    const wasSelected = selectedShotIds.includes(shotId);

    const newShotIds = (() => {
      if (wasSelected) {
        return selectedShotIds.filter(id => id !== shotId);
      }
      return [...selectedShotIds, shotId];
    })();

    setSelectedShotIds(newShotIds);
  };

  const toggleSelectionInGroup = (shotId: string) => {
    const index = selectedShotIds.indexOf(shotId);

    if (index === -1) {
      setSelectedShotIds([...selectedShotIds, shotId]);
    } else {
      const shallow = [...selectedShotIds];
      shallow.splice(index, 1);
      setSelectedShotIds(shallow);
    }
  };

  const multiSelectTo = (newShotId: string) => {
    const updated = multiSelect(
      overview.shots,
      selectedShotIds,
      newShotId
    );

    if (updated == null) {
      return;
    }

    setSelectedShotIds(updated);
  };

  const multiSelect = (shots: any[], selectedShotIds: string[], newShotId: string) => {
    if (!selectedShotIds.length) {
      return [newShotId];
    }

    const indexOfNew: number = shots.findIndex(shot => shot.id === newShotId);
    const indexOfLast: number = shots.findIndex(
      shot => shot.id === selectedShotIds[selectedShotIds.length - 1]
    );

    if (indexOfNew === indexOfLast) {
      return null;
    }

    const start: number = Math.min(indexOfLast, indexOfNew);
    const end: number = Math.max(indexOfLast, indexOfNew);

    const inBetween = shots.slice(start, end + 1);

    const toAdd = inBetween.filter(shot => {
      return !selectedShotIds.includes(shot.id);
    });

    const sorted = [...selectedShotIds, ...toAdd.map(shot => shot.id)];
    return sorted;
  };

  const handleDeleteSelected = () => {
    if (selectedShotIds.length === 0) return;
    confirm(
      () => deleteMultipleShotsLocal(filmId, sceneId, selectedShotIds),
      null,
      `Are you sure you want to delete ${selectedShotIds.length} shot${selectedShotIds.length !== 1 ? 's' : ''}?`,
      "Delete Shots"
    );
  };

  const deleteMultipleShotsLocal = (filmId: string, sceneId: string, shotIds: string[]) => {
    deleteMultipleShots(filmId, sceneId, shotIds);
    setSelectedShotIds([]);
  };

  const toggleSelectAll = () => {
    if (selectedShotIds.length === overview.shots?.length) {
      setSelectedShotIds([]);
    } else {
      setSelectedShotIds(overview.shots?.map((s: any) => s.id) || []);
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      // dropped outside the list
      return;
    }

    updateShotOrder(
      result.source.index,
      result.destination.index,
      result.source.droppableId,
      result.destination.droppableId
    );
  };

  function ShotsList({ deleteing }: { deleteing: boolean }) {
    return (
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable" >
          {(provided, snapshot) => (
            <table className={`${OnboardingStep3ClassName} table`}
              {...provided.droppableProps}
              ref={provided.innerRef}>
              <thead>
                <tr>
                  {!readOnly && (
                    <th>
                      <FilmanizeCheckbox
                        checked={selectedShotIds.length === (overview.shots?.length || 0) && (overview.shots?.length || 0) > 0}
                        disabled={deleteing}
                        onChange={toggleSelectAll}
                      />
                    </th>
                  )}
                  <th>Number</th>
                  <th>Size</th>
                  <th>Lens</th>
                  <th>Characters</th>
                  <th>Background Characters</th>
                </tr>
              </thead>
              <tbody>
                {(overview.shots || []).map((shot, index) => {
                  return (
                    <Shot
                      key={index}
                      index={index}
                      shot={shot}
                      filmId={filmId}
                      sceneId={sceneId}
                      readOnly={readOnly}
                      isSelected={selectedShotIds.includes(shot.id)}
                      toggleSelection={toggleSelection}
                      toggleSelectionInGroup={toggleSelectionInGroup}
                      multiSelectTo={multiSelectTo}
                      deleteing={deleteing} />
                  );
                })}
                {provided.placeholder}
              </tbody>
            </table>
          )}
        </Droppable>
      </DragDropContext>
    );
  }


  return (
    <div>
      {errors && <Errors errors={errors} />}
      {warnings && <Errors errors={warnings} isWarning={true} />}
      {deleteing && <Loader />}
      <DndProvider backend={HTML5Backend}>
        <div className={`columns ${OnboardingStep1ClassName}`}>
          <div className="column">
            <ShotsList deleteing={deleteing} />
          </div>
        </div>
      </DndProvider>
      <div className='buttons sticky-buttons mt-1'>
        <button
          type="submit"
          className={`button ${OnboardingStep2ClassName} ${saving ? "is-loading" : ""}`}
          disabled={pristine}
          onClick={() => saveShotsOrder(overview.shots.map(shot => shot.id))}>
          Save
        </button>
        {csvData && <CSVLink className="button" data={csvData} filename={`${filmName} Scene ${sceneNumber} Shots`}>Download CSV</CSVLink>}
        {!readOnly && overview.shots?.length > 0 && (
          <button
            className={`button is-danger ${deleteing ? "is-loading" : ""}`}
            onClick={handleDeleteSelected}
            disabled={selectedShotIds.length === 0}
          >
            Delete Selected ({selectedShotIds.length})
          </button>
        )}
      </div>
      <Onboarding onboardingSteps={shotsOnboardingSteps} section="Shots Overview" />
    </div>
  );
}